import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Plus,
  RotateCcw,
  Trophy,
} from "lucide-react";

const STORAGE_KEY = "catchcompass:scout-and-choose:v2";

type Category = "fish" | "habitat" | "conditions" | "pressure" | "confidence";
type TargetSpecies = "carp" | "catfish" | "both";
type FlowMode = "scout-lake" | "choose-peg";

type ObservationOption = {
  id: string;
  label: string;
  weight: number;
  category: Category;
};

type Lake = {
  id: number | string;
  name: string;
};

type SwimDefinition = {
  id: string;
  name: string;
  source?: "private" | "history" | "verified_seed" | "verified_owner" | "verified_community" | "pending_review" | "review_rejected" | string;
  latitude?: number | null;
  longitude?: number | null;
  accuracy_m?: number | null;
  captured_at?: string | null;
};

type MappingPolicy = {
  owner_direct_publish: boolean;
  lake_can_publish: boolean;
  community_requires_review: boolean;
};

const DEFAULT_MAPPING_POLICY: MappingPolicy = {
  owner_direct_publish: false,
  lake_can_publish: false,
  community_requires_review: true,
};

type SwimState = {
  selected: string[];
  scoutedAt?: string;
  taken?: boolean;
  chosen?: boolean;
};

type PersistedState = Record<string, SwimState>;
type PersistedByLake = Record<string, PersistedState>;

const OBSERVATIONS: ObservationOption[] = [
  { id: "repeated-showing", label: "Repeated showing fish", weight: 25, category: "fish" },
  { id: "showing", label: "Showing fish", weight: 18, category: "fish" },
  { id: "boshing", label: "Fish crashed / boshed", weight: 15, category: "fish" },
  { id: "fizzing", label: "Bubbling / fizzing", weight: 14, category: "fish" },
  { id: "cruising", label: "Fish cruising", weight: 11, category: "fish" },
  { id: "edge-fish", label: "Fish seen in edge", weight: 10, category: "fish" },
  { id: "spawning-fish", label: "Fish spawning", weight: 0, category: "fish" },
  { id: "no-visible", label: "No visible fish activity", weight: -2, category: "fish" },

  { id: "lily-pads", label: "Lily pads", weight: 3, category: "habitat" },
  { id: "weed", label: "Weed beds", weight: 3, category: "habitat" },
  { id: "overhang", label: "Overhanging trees", weight: 3, category: "habitat" },
  { id: "reeds", label: "Reeds / rushes", weight: 2, category: "habitat" },
  { id: "island", label: "Island / bar / point", weight: 3, category: "habitat" },
  { id: "island-channel", label: "Channel between islands", weight: 4, category: "habitat" },
  { id: "margin-shelf", label: "Marginal shelf", weight: 2, category: "habitat" },
  { id: "snags", label: "Snags", weight: 2, category: "habitat" },

  { id: "warm-wind", label: "Warm sustained wind pushing in", weight: 6, category: "conditions" },
  { id: "wind-pushing", label: "Wind pushing in", weight: 4, category: "conditions" },
  { id: "surface-chop", label: "Surface chop", weight: 2, category: "conditions" },
  { id: "fresh-inflow", label: "Fresh inflow", weight: 4, category: "conditions" },
  { id: "oxygenated", label: "Oxygenated area", weight: 4, category: "conditions" },
  { id: "cold-shelter", label: "Sheltered from cold wind", weight: 3, category: "conditions" },
  { id: "cold-exposed", label: "Cold adverse wind exposure", weight: -4, category: "conditions" },
  { id: "hot-shallow", label: "Very hot + shallow / exposed", weight: -3, category: "conditions" },

  { id: "quiet", label: "Quiet area", weight: 3, category: "pressure" },
  { id: "good-coverage", label: "Good water coverage", weight: 2, category: "pressure" },
  { id: "recent-pressure", label: "Recent angler pressure", weight: -2, category: "pressure" },
  { id: "heavy-pressure", label: "Looks heavily pressured", weight: -4, category: "pressure" },
  { id: "limited-coverage", label: "Limited water coverage", weight: -2, category: "pressure" },
  { id: "awkward", label: "Awkward to fish", weight: -2, category: "pressure" },

  { id: "love-it", label: "Love it", weight: 3, category: "confidence" },
  { id: "like-it", label: "Like it", weight: 1, category: "confidence" },
  { id: "neutral-feel", label: "Neutral / unsure", weight: 0, category: "confidence" },
  { id: "dont-fancy", label: "Don’t fancy it", weight: -2, category: "confidence" },
  { id: "hard-no", label: "Hard no", weight: 0, category: "confidence" },
];

const CAPS: Record<Category, { min: number; max: number }> = {
  fish: { min: -4, max: 25 },
  habitat: { min: -6, max: 10 },
  conditions: { min: -15, max: 15 },
  pressure: { min: -8, max: 6 },
  confidence: { min: -3, max: 3 },
};

const PREFERENCE_IDS = OBSERVATIONS
  .filter((option) => option.category === "confidence")
  .map((option) => option.id);

const POSITIVE_FISH_ACTIVITY_IDS = [
  "repeated-showing",
  "showing",
  "boshing",
  "fizzing",
  "cruising",
  "edge-fish",
  "spawning-fish",
];

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function todayInput() {
  const now = new Date();
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function currentTimeInput() {
  const now = new Date();
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function scoreSelected(selected: string[]) {
  // Fish welfare is an absolute override, not just another negative weight.
  if (selected.includes("spawning-fish")) return 0;

  const byCategory: Record<Category, number> = {
    fish: 0,
    habitat: 0,
    conditions: 0,
    pressure: 0,
    confidence: 0,
  };

  for (const id of selected) {
    const option = OBSERVATIONS.find((item) => item.id === id);
    if (option) byCategory[option.category] += option.weight;
  }

  const contribution = (Object.keys(byCategory) as Category[]).reduce(
    (sum, category) => {
      const cap = CAPS[category];
      return sum + clamp(byCategory[category], cap.min, cap.max);
    },
    0,
  );

  return clamp(50 + contribution, 0, 100);
}

function evidenceConfidence(selectedCount: number) {
  if (selectedCount === 0) return "Not scouted";
  if (selectedCount >= 4) return "Medium live confidence";
  return "Low live confidence";
}

function reasonFor(swim: SwimDefinition, selected: string[], score: number) {
  if (selected.length === 0) return "Not scouted yet.";
  if (selected.includes("spawning-fish"))
    return `Fish are spawning in ${swim.name}. For fish safety, CatchCompass recommends you don’t fish this swim.`;
  if (selected.includes("hard-no"))
    return `${swim.name} is excluded because you marked it Hard no.`;

  const options = selected
    .map((id) => OBSERVATIONS.find((option) => option.id === id))
    .filter(Boolean) as ObservationOption[];
  const positives = options
    .filter((option) => option.weight > 0)
    .sort((a, b) => b.weight - a.weight);
  const negatives = options
    .filter((option) => option.weight < 0)
    .sort((a, b) => a.weight - b.weight);

  if (score >= 60 && positives.length) {
    return `${swim.name} ranks well because you logged ${positives
      .slice(0, 2)
      .map((option) => option.label.toLowerCase())
      .join(" plus ")}.`;
  }
  if (score < 50 && negatives.length) {
    return `${swim.name} is held back by ${negatives
      .slice(0, 2)
      .map((option) => option.label.toLowerCase())
      .join(" and ")}.`;
  }
  if (positives.length) {
    return `${swim.name} has support from ${positives
      .slice(0, 2)
      .map((option) => option.label.toLowerCase())
      .join(" and ")}, but no dominant signal yet.`;
  }
  return `${swim.name} has no strong positive signal logged yet.`;
}

function loadState(): PersistedByLake {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedByLake) : {};
  } catch {
    return {};
  }
}

export default function ScoutAndChoosePage() {
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedLakeId = params.get("lake_id") ?? "";
  const requestedSpecies = params.get("species");
  const requestedSessionId = params.get("session_id") ?? "";
  const moveMode = params.get("mode") === "move" && Boolean(requestedSessionId);
  const fishingDate = params.get("date") || todayInput();
  const startTime = params.get("start") || currentTimeInput();
  const durationHours = Number(params.get("duration") || "24") || 24;

  const [lakes, setLakes] = useState<Lake[]>([]);
  const [lakeId, setLakeId] = useState(requestedLakeId);
  const [swims, setSwims] = useState<SwimDefinition[]>([]);
  const [mappingPolicy, setMappingPolicy] = useState<MappingPolicy>(DEFAULT_MAPPING_POLICY);
  const [stateByLake, setStateByLake] = useState<PersistedByLake>(() => loadState());
  const [editingSwimId, setEditingSwimId] = useState<string | null>(null);
  const [flowMode, setFlowMode] = useState<FlowMode | null>(null);
  const [directSwimId, setDirectSwimId] = useState<string | null>(null);
  const [autoAdviceSwimId, setAutoAdviceSwimId] = useState<string | null>(null);
  const [targetSpecies, setTargetSpecies] = useState<TargetSpecies>(
    requestedSpecies === "carp" ||
      requestedSpecies === "catfish" ||
      requestedSpecies === "both"
      ? requestedSpecies
      : "both",
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [adviceError, setAdviceError] = useState("");
  const [moveSaving, setMoveSaving] = useState(false);

  const [showAddLake, setShowAddLake] = useState(false);
  const [newLakeName, setNewLakeName] = useState("");
  const [newLakeLocation, setNewLakeLocation] = useState("");
  const [lakeAddError, setLakeAddError] = useState("");
  const [addingLake, setAddingLake] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newSwimName, setNewSwimName] = useState("");
  const [gps, setGps] = useState<{
    latitude: number;
    longitude: number;
    accuracy: number;
    capturedAt: string;
  } | null>(null);
  const [gpsStatus, setGpsStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewSubmittingSwimId, setReviewSubmittingSwimId] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateByLake));
  }, [stateByLake]);

  useEffect(() => {
    let cancelled = false;
    fetch("/app-api/lakes")
      .then((response) => {
        if (!response.ok) throw new Error("Could not load My Lakes");
        return response.json();
      })
      .then((rows: Lake[]) => {
        if (cancelled) return;
        setLakes(rows);
        setLakeId((current) => {
          if (current && rows.some((lake) => String(lake.id) === current)) return current;
          return rows.length ? String(rows[0].id) : "";
        });
      })
      .catch(() => {
        if (!cancelled) setLoadError("Could not load your lakes.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function addLake(event: React.FormEvent) {
    event.preventDefault();
    if (!newLakeName.trim() || addingLake) return;
    setAddingLake(true);
    setLakeAddError("");
    try {
      const response = await fetch("/app-api/lakes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newLakeName.trim(),
          location: newLakeLocation.trim() || null,
          latitude: null,
          longitude: null,
          notes: "",
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.id)
        throw new Error(data.error || "Could not add lake");

      const lake: Lake = { id: data.id, name: newLakeName.trim() };
      setLakes((current) => [...current, lake]);
      setNewLakeName("");
      setNewLakeLocation("");
      setShowAddLake(false);
      setLakeId(String(data.id));
    } catch (err) {
      setLakeAddError(err instanceof Error ? err.message : "Could not add lake");
    } finally {
      setAddingLake(false);
    }
  }

  async function loadSwims(selectedLakeId: string) {
    if (!selectedLakeId) {
      setSwims([]);
      setMappingPolicy(DEFAULT_MAPPING_POLICY);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const response = await fetch(
        `/app-api/scout/swims?lake_id=${encodeURIComponent(selectedLakeId)}`,
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not load pegs");
      setSwims(Array.isArray(data.swims) ? data.swims : []);
      setMappingPolicy({
        owner_direct_publish: data?.mapping_policy?.owner_direct_publish === true,
        lake_can_publish: data?.mapping_policy?.lake_can_publish === true,
        community_requires_review: data?.mapping_policy?.community_requires_review !== false,
      });
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load pegs");
      setSwims([]);
      setMappingPolicy(DEFAULT_MAPPING_POLICY);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (lakeId) loadSwims(lakeId);
    else setLoading(false);
    setEditingSwimId(null);
    setDirectSwimId(null);
    setAutoAdviceSwimId(null);
    setFlowMode(null);
    setAdviceError("");
    if (lakeId) {
      setStateByLake((current) => {
        const currentLake = current[lakeId];
        if (!currentLake) return current;
        const nextLake: PersistedState = {};
        for (const [swimId, value] of Object.entries(currentLake)) {
          nextLake[swimId] = { ...value, chosen: false };
        }
        return { ...current, [lakeId]: nextLake };
      });
    }
  }, [lakeId]);

  const lakeState = stateByLake[lakeId] ?? {};
  const selectedLake = lakes.find((lake) => String(lake.id) === lakeId) ?? null;

  const scored = useMemo(
    () =>
      swims.map((swim) => {
        const swimState = lakeState[swim.id] ?? { selected: [] };
        const score = scoreSelected(swimState.selected ?? []);
        const hardNo = (swimState.selected ?? []).includes("hard-no");
        const safetyHold = (swimState.selected ?? []).includes("spawning-fish");
        return {
          ...swim,
          state: swimState,
          score,
          hardNo,
          safetyHold,
          reason: reasonFor(swim, swimState.selected ?? [], score),
          confidence: evidenceConfidence((swimState.selected ?? []).length),
        };
      }),
    [swims, lakeState],
  );

  const rankedScouted = useMemo(
    () =>
      scored
        .filter((swim) => Boolean(swim.state.scoutedAt) && !swim.hardNo)
        .sort((a, b) => {
          const aUnavailable = Boolean(a.state.taken) || a.safetyHold;
          const bUnavailable = Boolean(b.state.taken) || b.safetyHold;
          if (aUnavailable !== bUnavailable) return aUnavailable ? 1 : -1;
          return b.score - a.score || a.name.localeCompare(b.name);
        }),
    [scored],
  );

  const excludedScouted = scored
    .filter((swim) => Boolean(swim.state.scoutedAt) && swim.hardNo)
    .sort((a, b) => a.name.localeCompare(b.name));
  const unscouted = scored.filter((swim) => !swim.state.scoutedAt);
  const bestAvailable = rankedScouted.find(
    (swim) => !swim.state.taken && !swim.safetyHold,
  );
  const chosen = scored.find(
    (swim) => swim.state.chosen && !swim.hardNo && !swim.safetyHold,
  );
  const editing = swims.find((swim) => swim.id === editingSwimId) ?? null;
  const editingState = editing ? lakeState[editing.id] ?? { selected: [] } : null;
  const availableCount = rankedScouted.filter(
    (swim) => !swim.state.taken && !swim.safetyHold,
  ).length;

  function updateSwim(swimId: string, updater: (current: SwimState) => SwimState) {
    setStateByLake((current) => {
      const currentLake = current[lakeId] ?? {};
      return {
        ...current,
        [lakeId]: {
          ...currentLake,
          [swimId]: updater(currentLake[swimId] ?? { selected: [] }),
        },
      };
    });
  }

  function toggleObservation(swimId: string, observationId: string) {
    const option = OBSERVATIONS.find((item) => item.id === observationId);
    updateSwim(swimId, (current) => {
      const exists = current.selected.includes(observationId);
      let selected: string[];
      if (option?.category === "confidence") {
        const withoutPreference = current.selected.filter(
          (id) => !PREFERENCE_IDS.includes(id),
        );
        selected = exists ? withoutPreference : [...withoutPreference, observationId];
      } else if (observationId === "no-visible") {
        const withoutPositiveFish = current.selected.filter(
          (id) => !POSITIVE_FISH_ACTIVITY_IDS.includes(id),
        );
        selected = exists
          ? withoutPositiveFish.filter((id) => id !== "no-visible")
          : [...withoutPositiveFish.filter((id) => id !== "no-visible"), "no-visible"];
      } else if (POSITIVE_FISH_ACTIVITY_IDS.includes(observationId)) {
        const withoutNoVisible = current.selected.filter((id) => id !== "no-visible");
        selected = exists
          ? withoutNoVisible.filter((id) => id !== observationId)
          : [...withoutNoVisible, observationId];
      } else {
        selected = exists
          ? current.selected.filter((id) => id !== observationId)
          : [...current.selected, observationId];
      }
      return {
        ...current,
        selected,
        chosen:
          selected.includes("hard-no") || selected.includes("spawning-fish")
            ? false
            : current.chosen,
        scoutedAt: new Date().toISOString(),
      };
    });
  }

  function markTaken(swimId: string) {
    setAdviceError("");
    updateSwim(swimId, (current) => ({
      ...current,
      taken: !current.taken,
      chosen: false,
    }));
  }

  function chooseSwim(swimId: string) {
    const candidate = scored.find((swim) => swim.id === swimId);
    if (candidate?.safetyHold || candidate?.hardNo) return;
    setAdviceError("");
    setStateByLake((current) => {
      const currentLake = current[lakeId] ?? {};
      const nextLake: PersistedState = { ...currentLake };
      for (const swim of swims) {
        const value = currentLake[swim.id] ?? { selected: [] };
        nextLake[swim.id] = { ...value, chosen: swim.id === swimId };
      }
      return { ...current, [lakeId]: nextLake };
    });
  }

  function clearChosen() {
    if (!chosen) return;
    updateSwim(chosen.id, (current) => ({ ...current, chosen: false }));
  }

  function scoutChosenPeg(swimId: string) {
    setAdviceError("");
    setDirectSwimId(swimId);
    updateSwim(swimId, (current) => ({
      ...current,
      selected: [],
      scoutedAt: undefined,
      chosen: false,
    }));
    setEditingSwimId(swimId);
  }

  function chooseAndBuildAdvice(swimId: string) {
    chooseSwim(swimId);
    setAutoAdviceSwimId(swimId);
  }

  function finishScout(swimId: string) {
    setEditingSwimId(null);
    if (directSwimId === swimId) {
      setDirectSwimId(null);
      chooseAndBuildAdvice(swimId);
    }
  }

  function resetLake() {
    if (!lakeId) return;
    if (!window.confirm("Clear Scout & Choose observations for this lake on this device?"))
      return;
    setStateByLake((current) => ({ ...current, [lakeId]: {} }));
    setEditingSwimId(null);
    setAdviceError("");
  }

  function useCurrentLocation() {
    if (!navigator.geolocation) {
      setGpsStatus("error");
      setAddError("This device does not support location access.");
      return;
    }
    setGpsStatus("loading");
    setAddError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGps({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          capturedAt: new Date(position.timestamp).toISOString(),
        });
        setGpsStatus("ready");
      },
      () => {
        setGpsStatus("error");
        setAddError(
          "Could not capture your location. You can still save the peg by name.",
        );
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  }

  async function addSwim(e: React.FormEvent) {
    e.preventDefault();
    if (!lakeId || !newSwimName.trim() || adding) return;
    setAdding(true);
    setAddError("");
    try {
      const response = await fetch("/app-api/scout/swims", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lake_id: Number(lakeId),
          name: newSwimName.trim(),
          latitude: gps?.latitude ?? null,
          longitude: gps?.longitude ?? null,
          accuracy_m: gps?.accuracy ?? null,
          captured_at: gps?.capturedAt ?? null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save this peg");
      await loadSwims(lakeId);
      setShowAdd(false);
      setNewSwimName("");
      setGps(null);
      setGpsStatus("idle");
      if (flowMode === "choose-peg") setDirectSwimId(String(data.id));
      setEditingSwimId(String(data.id));
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Could not save this peg");
    } finally {
      setAdding(false);
    }
  }

  async function submitSwimForReview(swimId: string) {
    if (!lakeId || reviewSubmittingSwimId) return;
    const numericId = Number(swimId.startsWith("saved:") ? swimId.slice(6) : "");
    if (!Number.isFinite(numericId) || numericId <= 0) {
      setReviewError("This peg cannot be submitted for review.");
      return;
    }

    setReviewSubmittingSwimId(swimId);
    setReviewError("");
    try {
      const response = await fetch(
        `/app-api/scout/swims/${numericId}/submit-review`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Could not submit this peg for review");
      await loadSwims(lakeId);
    } catch (err) {
      setReviewError(
        err instanceof Error ? err.message : "Could not submit this peg for review",
      );
    } finally {
      setReviewSubmittingSwimId(null);
    }
  }

  async function getFishingAdvice() {
    if (!chosen || !lakeId || adviceLoading || moveSaving) return;
    setAdviceLoading(true);
    setAdviceError("");

    try {
      let weather: Record<string, any> = {};
      try {
        const weatherResponse = await fetch(
          `/app-api/advisor/current-weather?lake_id=${encodeURIComponent(lakeId)}`,
        );
        if (weatherResponse.ok) {
          const data = await weatherResponse.json();
          if (data.ok) {
            weather = {
              forecast_air_temp_c: data.air_temp_c,
              forecast_pressure_hpa: data.pressure_hpa,
              forecast_pressure_trend: data.pressure_trend || "",
              forecast_wind_direction: data.wind_direction,
              forecast_wind_speed_mph: data.wind_speed_mph,
              forecast_cloud_pct: data.cloud_pct,
              forecast_precipitation_mm: data.precipitation_mm,
            };
          }
        }
      } catch {
        // Weather is optional. Scout evidence remains useful without it.
      }

      const buildAdvice = async (species: "carp" | "catfish") => {
        const response = await fetch("/app-api/advisor/recommendations", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            is_future_trip: false,
            lake_id: Number(lakeId),
            fishing_date: fishingDate,
            expected_start_time: startTime,
            expected_duration_hours: durationHours,
            target_species: species,
            fixed_swim_name: chosen.name,
            scout_observation_ids: chosen.state.selected ?? [],
            scout_score: chosen.score,
            scout_confidence: chosen.confidence,
            forecast_air_temp_c: weather.forecast_air_temp_c ?? null,
            forecast_pressure_hpa: weather.forecast_pressure_hpa ?? null,
            forecast_pressure_trend: weather.forecast_pressure_trend ?? "",
            forecast_wind_direction: weather.forecast_wind_direction ?? null,
            forecast_wind_speed_mph: weather.forecast_wind_speed_mph ?? null,
            forecast_cloud_pct: weather.forecast_cloud_pct ?? null,
            forecast_precipitation_mm: weather.forecast_precipitation_mm ?? null,
          }),
        });
        const data = await response.json();
        if (!response.ok || !data.id)
          throw new Error(data.error || `Could not create ${species} fishing advice`);
        return data;
      };

      const moveLiveSession = async () => {
        if (!moveMode || !requestedSessionId) return;
        setMoveSaving(true);
        const response = await fetch(
          `/app-api/sessions/${encodeURIComponent(requestedSessionId)}/move-peg`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              peg: chosen.name,
              scout_observation_ids: chosen.state.selected ?? [],
              scout_score: chosen.score,
              scout_confidence: chosen.confidence,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.error || "Could not move the live session");
      };

      const linkAdvice = async (adviceId: number | string) => {
        if (!moveMode || !requestedSessionId) return;
        const response = await fetch(
          `/app-api/advisor/recommendations/${encodeURIComponent(String(adviceId))}/link`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ actual_session_id: Number(requestedSessionId) }),
          },
        );
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.error || "Could not link updated advice");
      };

      if (targetSpecies === "both") {
        const [carpAdvice, catfishAdvice] = await Promise.all([
          buildAdvice("carp"),
          buildAdvice("catfish"),
        ]);
        await moveLiveSession();
        if (moveMode) {
          await linkAdvice(catfishAdvice.id);
          await linkAdvice(carpAdvice.id);
        }
        navigate(
          `/advisor/both?carp=${encodeURIComponent(String(carpAdvice.id))}&catfish=${encodeURIComponent(String(catfishAdvice.id))}`,
        );
      } else {
        const advice = await buildAdvice(targetSpecies);
        await moveLiveSession();
        if (moveMode) await linkAdvice(advice.id);
        navigate(`/advisor/${advice.id}`);
      }
    } catch (err) {
      setAdviceError(
        err instanceof Error ? err.message : "Could not create fishing advice",
      );
    } finally {
      setMoveSaving(false);
      setAdviceLoading(false);
    }
  }

  useEffect(() => {
    if (
      !autoAdviceSwimId ||
      !chosen ||
      chosen.id !== autoAdviceSwimId ||
      adviceLoading ||
      moveSaving
    )
      return;
    setAutoAdviceSwimId(null);
    void getFishingAdvice();
  }, [autoAdviceSwimId, chosen?.id, adviceLoading, moveSaving]);

  const groups: Array<{ category: Category; title: string }> = [
    { category: "fish", title: "Fish activity" },
    { category: "habitat", title: "Features" },
    { category: "conditions", title: "Local conditions" },
    { category: "pressure", title: "Pressure / practicality" },
    { category: "confidence", title: "Your preference" },
  ];

  return (
    <div className="mx-auto w-full max-w-3xl px-3 py-4 pb-28 sm:px-6 sm:py-6">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
          Scout &amp; Choose
        </p>
        <h1 className="mt-1 text-2xl font-bold text-primary">
          {moveMode ? "Move Peg" : "Start Fishing"}
        </h1>
        <p className="mt-2 text-sm leading-5 text-secondary">
          {moveMode
            ? "Your current session will continue. CatchCompass will record how long you spend in each peg."
            : "Choose how you want to find your peg. Either compare the lake or pick a peg you already know, then scout that peg before CatchCompass builds your plan."}
        </p>
      </div>

      <section className="mb-4 rounded-xl border border-border bg-raised p-3 sm:p-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-secondary">
          Lake
        </label>
        {lakes.length ? (
          <>
            <select
              value={lakeId}
              onChange={(event) => setLakeId(event.target.value)}
              disabled={moveMode}
              className="mt-2 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-primary disabled:opacity-60"
            >
              {lakes.map((lake) => (
                <option key={lake.id} value={String(lake.id)}>
                  {lake.name}
                </option>
              ))}
            </select>
            {!moveMode ? (
              <button
                type="button"
                onClick={() => {
                  setLakeAddError("");
                  setShowAddLake(true);
                }}
                className="mt-2 min-h-10 text-sm font-semibold text-accent"
              >
                + Add another lake
              </button>
            ) : null}
          </>
        ) : (
          <div className="mt-2 rounded-xl border border-dashed border-border bg-background p-4 text-center">
            <p className="text-sm font-semibold text-primary">No lakes saved yet</p>
            <p className="mt-1 text-sm text-secondary">
              Add the lake you are fishing and it will also be saved in My Lakes.
            </p>
            <button
              type="button"
              onClick={() => {
                setLakeAddError("");
                setShowAddLake(true);
              }}
              className="mt-3 min-h-11 rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white"
            >
              Add your first lake
            </button>
          </div>
        )}

        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-secondary">
            Target species
          </p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {(["carp", "catfish", "both"] as TargetSpecies[]).map((species) => (
              <button
                key={species}
                type="button"
                onClick={() => setTargetSpecies(species)}
                className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-semibold capitalize ${
                  targetSpecies === species
                    ? "border-accent bg-accent text-white"
                    : "border-border bg-background text-primary"
                }`}
                aria-pressed={targetSpecies === species}
              >
                {species}
              </button>
            ))}
          </div>
        </div>

      </section>

      {loadError ? (
        <p className="mb-4 rounded-lg bg-error-weak p-3 text-sm text-error">{loadError}</p>
      ) : null}

      {adviceError ? (
        <div className="mb-4 rounded-xl border border-error bg-error-weak p-4">
          <p className="text-sm font-semibold text-error">Could not build your plan.</p>
          <p className="mt-1 text-sm text-error">{adviceError}</p>
          {chosen ? (
            <button
              type="button"
              onClick={() => void getFishingAdvice()}
              className="mt-3 min-h-11 rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white"
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : null}

      {adviceLoading || moveSaving ? (
        <div className="mb-4 rounded-xl border border-accent/40 bg-raised p-4">
          <p className="text-sm font-semibold text-primary">
            {moveMode ? "Updating your session…" : "Building your plan…"}
          </p>
          <p className="mt-1 text-sm text-secondary">
            {moveMode
              ? "Your previous peg time is being kept while CatchCompass rebuilds advice for the new peg."
              : "CatchCompass is turning your chosen peg and Scout observations into your starting plan."}
          </p>
        </div>
      ) : null}

      {!flowMode ? (
        <section className="mb-4 rounded-xl border border-border bg-raised p-4">
          <h2 className="text-lg font-bold text-primary">How are you choosing your peg?</h2>
          <p className="mt-1 text-sm leading-5 text-secondary">
            Both routes finish by scouting the peg you will actually fish.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              disabled={!lakeId}
              onClick={() => setFlowMode("scout-lake")}
              className="min-h-24 rounded-xl border border-accent bg-accent/10 p-4 text-left disabled:opacity-50"
            >
              <span className="block text-base font-bold text-primary">Scout the lake</span>
              <span className="mt-1 block text-sm leading-5 text-secondary">
                Compare several pegs using live signs, features, pressure and your judgement.
              </span>
            </button>
            <button
              type="button"
              disabled={!lakeId}
              onClick={() => setFlowMode("choose-peg")}
              className="min-h-24 rounded-xl border border-border bg-background p-4 text-left disabled:opacity-50"
            >
              <span className="block text-base font-bold text-primary">I already know my peg</span>
              <span className="mt-1 block text-sm leading-5 text-secondary">
                Pick it first, then Scout this peg so the Advisor can see the features and live signs.
              </span>
            </button>
          </div>
        </section>
      ) : (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-border bg-inset p-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-secondary">Current route</p>
            <p className="mt-1 text-sm font-semibold text-primary">
              {flowMode === "scout-lake" ? "Scout the lake" : "Choose peg, then scout it"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setFlowMode(null);
              setDirectSwimId(null);
              clearChosen();
            }}
            className="min-h-10 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-secondary"
          >
            Change
          </button>
        </div>
      )}

      {flowMode === "choose-peg" ? (
        <section className="mb-5">
          <div className="mb-3">
            <h2 className="text-lg font-bold text-primary">Choose your peg</h2>
            <p className="text-sm text-secondary">
              Selecting it does not skip scouting. The next screen is Scout this peg.
            </p>
          </div>
          {loading ? (
            <div className="rounded-xl border border-border bg-raised p-4 text-sm text-secondary">
              Loading pegs…
            </div>
          ) : swims.length ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {swims.map((swim) => (
                <button
                  key={swim.id}
                  type="button"
                  onClick={() => scoutChosenPeg(swim.id)}
                  className="flex min-h-14 items-center justify-between rounded-xl border border-border bg-raised px-4 py-3 text-left"
                >
                  <span>
                    <span className="block text-sm font-semibold text-primary">{swim.name}</span>
                    <span className="block text-xs text-secondary">Next: Scout this peg</span>
                  </span>
                  <span className="ml-3 text-xs font-semibold text-accent">Choose</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-raised p-4 text-center">
              <p className="text-sm text-secondary">No pegs saved yet.</p>
              <button
                type="button"
                onClick={() => setShowAdd(true)}
                className="mt-3 min-h-11 rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white"
              >
                Add your peg
              </button>
            </div>
          )}
          {lakeId && swims.length ? (
            <button
              type="button"
              onClick={() => {
                setAddError("");
                setShowAdd(true);
              }}
              className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-accent px-3 py-2 text-sm font-semibold text-accent"
            >
              <Plus size={16} /> Add another peg
            </button>
          ) : null}
        </section>
      ) : null}

      {flowMode === "scout-lake" ? (
        <>
      <div className="mb-4 rounded-xl border border-accent/40 bg-raised p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <Trophy className="mt-0.5 shrink-0 text-accent" size={20} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-primary">Best available</p>
            {bestAvailable ? (
              <>
                <p className="mt-1 text-lg font-bold leading-6 text-primary">
                  {bestAvailable.name} · {bestAvailable.score}/100
                </p>
                <p className="mt-1 text-sm leading-5 text-secondary">
                  {bestAvailable.reason}
                </p>
                <p className="mt-1 text-xs text-tertiary">{bestAvailable.confidence}</p>
              </>
            ) : (
              <p className="mt-1 text-sm text-secondary">
                Scout a peg and the strongest available option will appear here.
              </p>
            )}
          </div>
        </div>
      </div>

      <section>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-primary">Ranked pegs</h2>
            <p className="text-xs text-secondary">
              {rankedScouted.length} ranked · {availableCount} available · {excludedScouted.length} excluded
            </p>
          </div>
          <span className="rounded-full bg-inset px-2.5 py-1 text-xs font-semibold text-secondary">
            Best first
          </span>
        </div>

        {loading ? (
          <div className="rounded-xl border border-border bg-raised p-4 text-sm text-secondary">
            Loading pegs…
          </div>
        ) : rankedScouted.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-raised p-4 text-sm text-secondary">
            No ranked pegs yet. Scout one below, or add a new peg while you walk.
          </div>
        ) : (
          <div className="space-y-3">
            {rankedScouted.map((swim, index) => {
              const isTaken = Boolean(swim.state.taken);
              const isSafetyHold = Boolean(swim.safetyHold);
              return (
                <article
                  key={swim.id}
                  className={`rounded-xl border p-3 sm:p-4 ${
                    isSafetyHold
                      ? "border-warning bg-warning-weak"
                      : isTaken
                        ? "border-border bg-inset opacity-60"
                        : "border-border bg-raised"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingSwimId(swim.id)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-md bg-inset px-2 py-1 text-xs font-bold text-secondary">
                          #{index + 1}
                        </span>
                        <h3 className="min-w-0 text-base font-bold text-primary">{swim.name}</h3>
                        {index === 0 && !isTaken && !isSafetyHold ? (
                          <span className="rounded-full border border-accent px-2 py-0.5 text-[11px] font-semibold text-accent">
                            Best available
                          </span>
                        ) : null}
                        {isSafetyHold ? (
                          <span className="rounded-full border border-warning px-2 py-0.5 text-[11px] font-semibold text-warning">
                            Spawning · don’t fish
                          </span>
                        ) : null}
                        {swim.state.chosen ? (
                          <span className="rounded-full border border-success px-2 py-0.5 text-[11px] font-semibold text-success">
                            Chosen
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs font-medium text-secondary">
                        {swim.score}/100 · {swim.confidence}
                      </p>
                      <p className="mt-2 text-sm leading-5 text-secondary">{swim.reason}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => markTaken(swim.id)}
                      className={`min-h-11 shrink-0 rounded-lg border px-3 py-2 text-xs font-bold ${isTaken ? "border-border bg-background text-secondary" : "border-warning/60 bg-warning-weak text-warning"}`}
                    >
                      {isTaken ? "Undo" : "Taken"}
                    </button>
                  </div>
                  {!isTaken && !isSafetyHold ? (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                      <button
                        type="button"
                        onClick={() => setEditingSwimId(swim.id)}
                        className="min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-primary"
                      >
                        Update
                      </button>
                      <button
                        type="button"
                        onClick={() => chooseAndBuildAdvice(swim.id)}
                        className="min-h-11 rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white"
                      >
                        Fish this peg
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {excludedScouted.length ? (
        <section className="mt-6">
          <h2 className="text-lg font-bold text-primary">Excluded by you</h2>
          <div className="mt-3 space-y-2">
            {excludedScouted.map((swim) => (
              <button
                key={swim.id}
                type="button"
                onClick={() => setEditingSwimId(swim.id)}
                className="flex min-h-12 w-full items-center justify-between rounded-xl border border-border bg-inset px-3 py-3 text-left opacity-75"
              >
                <span>
                  <span className="block text-sm font-semibold text-primary">{swim.name}</span>
                  <span className="block text-xs text-secondary">Hard no · excluded from ranking</span>
                </span>
                <span className="text-xs font-semibold text-accent">Update</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {unscouted.length ? (
        <section className="mt-6">
          <div className="mb-3">
            <h2 className="text-lg font-bold text-primary">Still to scout</h2>
            <p className="text-xs text-secondary">
              Tap a peg, log what you see, and it moves into the ranked list.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {unscouted.map((swim) => (
              <button
                key={swim.id}
                type="button"
                onClick={() => setEditingSwimId(swim.id)}
                className="flex min-h-12 items-center justify-between rounded-xl border border-border bg-raised px-3 py-3 text-left"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-primary">{swim.name}</span>
                  <span className="block text-xs text-secondary">
                    {swim.source === "private"
                      ? "Your saved peg"
                      : swim.source === "pending_review"
                        ? "Submitted for review"
                        : swim.source === "review_rejected"
                          ? "Your saved peg · review not approved"
                          : swim.source === "history"
                            ? "From your fishing history"
                            : swim.source === "verified_owner" ||
                                swim.source === "verified_community"
                              ? "Verified lake peg"
                              : swim.source === "verified_seed"
                                ? "Known venue swim"
                                : "Scout peg"}
                  </span>
                </span>
                <span className="ml-3 shrink-0 text-xs font-semibold text-accent">Scout</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {!loading && lakeId && swims.length === 0 ? (
        <section className="mt-6 rounded-xl border border-dashed border-border bg-raised p-4 text-center">
          <p className="text-sm font-semibold text-primary">No pegs saved for this lake yet</p>
          <p className="mt-1 text-sm text-secondary">
            Add the first one when you reach it. CatchCompass can use it immediately.
          </p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="mt-3 min-h-11 rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white"
          >
            Add first peg
          </button>
        </section>
      ) : null}

      {lakeId && swims.length ? (
        <button
          type="button"
          onClick={() => {
            setAddError("");
            setShowAdd(true);
          }}
          className="mt-6 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-accent px-3 py-2 text-sm font-semibold text-accent"
        >
          <Plus size={16} /> Add peg / swim to scout
        </button>
      ) : null}

      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="text-xs leading-5 text-tertiary">
          Live fish evidence carries more weight than static features.{" "}
          {mappingPolicy.owner_direct_publish
            ? mappingPolicy.lake_can_publish
              ? "Pegs you add here become shared verified lake pegs for everyone. Other anglers must submit theirs for your review."
              : "This lake is not linked to the CatchCompass directory yet, so new pegs cannot be shared across accounts."
            : "Your saved peg locations stay private unless you explicitly submit one for CatchCompass review."}
        </p>
        <button
          type="button"
          onClick={resetLake}
          className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-secondary"
        >
          <RotateCcw size={14} /> Reset
        </button>
      </div>
        </>
      ) : null}

      {editing && editingState ? (
        <div
          className="fixed inset-0 z-50 flex items-end bg-black/50 sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`Scout ${editing.name}`}
        >
          <div className="max-h-[88dvh] w-full overflow-y-auto rounded-t-2xl border border-border bg-background p-4 pb-8 sm:max-w-xl sm:rounded-2xl sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                  {selectedLake?.name || "Scout"}
                </p>
                <h2 className="mt-1 text-xl font-bold text-primary">{editing.name}</h2>
                <p className="mt-1 text-sm text-secondary">
                  Tap only what you can actually observe here now.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingSwimId(null)}
                className="min-h-11 shrink-0 rounded-lg border border-border px-3 py-2 text-sm text-primary"
              >
                Done
              </button>
            </div>

            <div className="mt-5 space-y-5">
              {groups.map((group) => (
                <section key={group.category}>
                  <h3 className="mb-2 text-sm font-semibold text-primary">{group.title}</h3>
                  {group.category === "confidence" ? (
                    <p className="mb-2 text-xs leading-5 text-tertiary">
                      Your judgement carries real weight. Pick one. Hard no removes the peg from the ranking.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    {OBSERVATIONS.filter((option) => option.category === group.category).map(
                      (option) => {
                        const active = editingState.selected.includes(option.id);
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => toggleObservation(editing.id, option.id)}
                            className={`min-h-11 rounded-full border px-3 py-2 text-sm font-medium ${active ? "border-accent bg-accent text-white" : "border-border bg-raised text-primary"}`}
                            aria-pressed={active}
                          >
                            {option.label}
                          </button>
                        );
                      },
                    )}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-6 rounded-xl bg-inset p-3">
              {editingState.selected.includes("spawning-fish") ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wider text-secondary">
                    Live Scout score
                  </p>
                  <p className="mt-1 text-2xl font-bold text-warning">0/100</p>
                  <p className="mt-1 text-sm text-secondary">
                    Fish are spawning in this swim. For fish safety, CatchCompass recommends you don’t fish this swim.
                  </p>
                </>
              ) : editingState.selected.includes("hard-no") ? (
                <>
                  <p className="text-xl font-bold text-warning">Excluded from ranking</p>
                  <p className="mt-1 text-sm text-secondary">
                    You marked this peg Hard no, so CatchCompass will not recommend it.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wider text-secondary">
                    Live Scout score
                  </p>
                  <p className="mt-1 text-2xl font-bold text-primary">
                    {scoreSelected(editingState.selected)}/100
                  </p>
                  <p className="mt-1 text-sm text-secondary">
                    {reasonFor(editing, editingState.selected, scoreSelected(editingState.selected))}
                  </p>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => finishScout(editing.id)}
              className="mt-5 min-h-12 w-full rounded-xl bg-accent px-4 py-3 text-sm font-bold text-white"
            >
              {directSwimId === editing.id ? "Save & use this peg" : "Save Scout observations"}
            </button>

            {!mappingPolicy.owner_direct_publish &&
            (editing.source === "private" || editing.source === "review_rejected") ? (
              <button
                type="button"
                onClick={() => submitSwimForReview(editing.id)}
                disabled={reviewSubmittingSwimId === editing.id}
                className="mt-3 min-h-11 w-full rounded-lg border border-border px-3 py-2 text-sm font-semibold text-primary disabled:opacity-60"
              >
                {reviewSubmittingSwimId === editing.id
                  ? "Submitting…"
                  : "Submit peg for review"}
              </button>
            ) : editing.source === "pending_review" ? (
              <p className="mt-3 rounded-lg bg-inset px-3 py-2 text-center text-sm text-secondary">
                This peg is waiting for CatchCompass admin review. It is still private until approved.
              </p>
            ) : null}

            {reviewError ? (
              <p className="mt-3 rounded-lg bg-error-weak px-3 py-2 text-sm text-error" role="alert">
                {reviewError}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {showAddLake ? (
        <div
          className="fixed inset-0 z-[56] flex items-end bg-black/50 sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Add lake"
        >
          <form
            onSubmit={addLake}
            className="w-full rounded-t-2xl border border-border bg-background p-5 pb-8 sm:max-w-md sm:rounded-2xl sm:p-6"
          >
            <h2 className="text-xl font-bold text-primary">Add a lake</h2>
            <p className="mt-1 text-sm text-secondary">
              Add it here and CatchCompass will save it to My Lakes automatically.
            </p>

            <label className="mt-5 block text-xs font-semibold uppercase tracking-wider text-secondary">
              Lake name
            </label>
            <input
              value={newLakeName}
              onChange={(event) => setNewLakeName(event.target.value)}
              placeholder="e.g. Stillwater Front Lake"
              maxLength={160}
              required
              autoFocus
              className="mt-2 min-h-11 w-full rounded-lg border border-border bg-raised px-3 py-2 text-sm text-primary"
            />

            <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-secondary">
              Location <span className="normal-case font-normal">(optional)</span>
            </label>
            <input
              value={newLakeLocation}
              onChange={(event) => setNewLakeLocation(event.target.value)}
              placeholder="Town or county"
              maxLength={160}
              className="mt-2 min-h-11 w-full rounded-lg border border-border bg-raised px-3 py-2 text-sm text-primary"
            />

            {lakeAddError ? (
              <p className="mt-3 rounded-lg bg-error-weak px-3 py-2 text-sm text-error" role="alert">
                {lakeAddError}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={addingLake || !newLakeName.trim()}
              className="mt-5 min-h-12 w-full rounded-xl bg-accent px-4 py-3 text-sm font-bold text-white disabled:opacity-60"
            >
              {addingLake ? "Adding lake…" : "Add lake & continue"}
            </button>
            <button
              type="button"
              disabled={addingLake}
              onClick={() => {
                setShowAddLake(false);
                setLakeAddError("");
              }}
              className="mt-3 min-h-11 w-full text-sm font-medium text-secondary disabled:opacity-50"
            >
              Cancel
            </button>
          </form>
        </div>
      ) : null}

      {showAdd ? (
        <div
          className="fixed inset-0 z-[55] flex items-end bg-black/50 sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Add peg or swim"
        >
          <form
            onSubmit={addSwim}
            className="w-full rounded-t-2xl border border-border bg-background p-5 pb-8 sm:max-w-md sm:rounded-2xl sm:p-6"
          >
            <h2 className="text-xl font-bold text-primary">Add a peg / swim</h2>
            <p className="mt-1 text-sm text-secondary">
              {mappingPolicy.owner_direct_publish
                ? mappingPolicy.lake_can_publish
                  ? "You are mapping this lake. Saving this peg makes it available to everyone who uses the same lake in CatchCompass."
                  : "Save it for Scout now. This lake must be linked to the CatchCompass directory before your peg can be shared across accounts."
                : "Save it privately and use it in Scout immediately. You can submit it for review later if you want it added for everyone."}
            </p>

            <label className="mt-5 block text-xs font-semibold uppercase tracking-wider text-secondary">
              Peg or swim name
            </label>
            <input
              value={newSwimName}
              onChange={(event) => setNewSwimName(event.target.value)}
              placeholder="e.g. Peg 7 or The Point"
              maxLength={120}
              required
              className="mt-2 min-h-11 w-full rounded-lg border border-border bg-raised px-3 py-2 text-sm text-primary"
            />

            <button
              type="button"
              onClick={useCurrentLocation}
              disabled={gpsStatus === "loading"}
              className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-border bg-raised px-3 py-2 text-sm font-semibold text-primary disabled:opacity-60"
            >
              <MapPin size={16} />
              {gpsStatus === "loading"
                ? "Getting location…"
                : gpsStatus === "ready"
                  ? "Location captured"
                  : "Use my current location"}
            </button>

            {gps ? (
              <p className="mt-2 text-xs text-secondary">
                GPS captured to about {Math.round(gps.accuracy)} m accuracy.
              </p>
            ) : (
              <p className="mt-2 text-xs text-tertiary">
                {mappingPolicy.owner_direct_publish && mappingPolicy.lake_can_publish
                  ? "Location is recommended. The peg name can still be shared without a GPS point."
                  : "Location is recommended but not required. The peg remains private unless it is reviewed and approved."}
              </p>
            )}

            {addError ? (
              <p className="mt-3 rounded-lg bg-error-weak px-3 py-2 text-sm text-error">
                {addError}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={!newSwimName.trim() || adding}
              className="mt-5 min-h-12 w-full rounded-xl bg-accent px-4 py-3 text-sm font-bold text-white disabled:opacity-60"
            >
              {adding ? "Saving…" : "Save peg and scout it"}
            </button>
            <button
              type="button"
              disabled={adding}
              onClick={() => {
                setShowAdd(false);
                setNewSwimName("");
                setGps(null);
                setGpsStatus("idle");
                setAddError("");
              }}
              className="mt-3 min-h-11 w-full text-sm font-medium text-secondary disabled:opacity-50"
            >
              Cancel
            </button>
          </form>
        </div>
      ) : null}

    </div>
  );
}
